import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './VerificacaoAndaime.css';

/* ============================================================
   REGISTO DE INCIDENTES / COMUNICAÇÕES — acesso mobile (P_GSI_03)
   Substitui o Microsoft Forms "SPROD - Registo de Comunicação /
   Incidentes". O trabalhador comunica ao RSI:
     · as 4 origens do Forms — acidente de trabalho, reclamação,
       não conformidade e outros (melhoria / sugestão);
     · e, em Segurança (SST), quase-acidentes e incidentes /
       condições perigosas;
   com testemunhas e fotos. Os campos obrigatórios são os do Forms
   (data, nome, departamento / área, origem, descrição, descrição do
   incidente, plano de ação e melhoria, a executar em), mais as regras
   de cada tipo (ex.: no acidente é obrigatório indicar quem se magoou).
   O registo entra em Sistema Integrado > Acidentes e Incidentes
   (origem 'mobile', estado 'aberto'), onde é investigado (RG.10) ou
   dá origem a uma NC. Aqui só se criam registos e se consultam os próprios.
   ============================================================ */

const MAX_FOTOS = 6;
const MAX_TESTEMUNHAS = 2;

// Origem da comunicação (tipo). grupo 'forms' = as 4 origens do Forms (aparecem primeiro);
// grupo 'seguranca' = restantes ocorrências SST. sst: há pessoa envolvida / sinistrado (e RG.10 no SGI).
const TIPOS = [
    {
        v: 'acidente', grupo: 'forms', sst: true, icone: '🚑', titulo: 'Acidente de trabalho', curto: 'Acidente de trabalho',
        sub: 'Alguém se magoou', cor: '#c53030', fundo: '#fff5f5',
        phCirc: 'Local, horas, quem estava presente (testemunhas), se teve tratamento hospitalar, se foram chamados os meios de socorro…',
        phDesc: 'O que se estava a fazer e como aconteceu o acidente…',
    },
    {
        v: 'reclamacao', grupo: 'forms', sst: false, icone: '🗣️', titulo: 'Reclamação', curto: 'Reclamação',
        sub: 'De um cliente ou de outra parte', cor: '#6b46c1', fundo: '#faf5ff',
        phCirc: 'Quem reclamou, quando e por que meio (telefone, email, em obra…)…',
        phDesc: 'Qual é a reclamação e sobre o quê…',
    },
    {
        v: 'nao_conformidade', grupo: 'forms', sst: false, icone: '❗', titulo: 'Não conformidade', curto: 'Não conformidade',
        sub: 'Algo não cumpre um requisito', cor: '#975a16', fundo: '#fffff0',
        phCirc: 'Onde e quando foi detetada, em que obra, serviço ou material…',
        phDesc: 'O que não está conforme e qual o requisito ou procedimento em causa…',
    },
    {
        v: 'outro', grupo: 'forms', sst: false, icone: '💡', titulo: 'Outros (melhoria / sugestão)', curto: 'Outros (melhoria / sugestão)',
        sub: 'Ideia para melhorar', cor: '#276749', fundo: '#f0fff4',
        phCirc: 'Em que situação surgiu, onde, quem está envolvido…',
        phDesc: 'Qual é a melhoria ou sugestão…',
    },
    {
        v: 'quase_acidente', grupo: 'seguranca', sst: true, icone: '⚠️', titulo: 'Quase-acidente', curto: 'Quase-acidente',
        sub: 'Podia ter havido lesão, ninguém se magoou', cor: '#c05621', fundo: '#fffaf0',
        phCirc: 'Local, horas, o que se estava a fazer, quem estava presente…',
        phDesc: 'O que aconteceu e o que podia ter acontecido…',
    },
    {
        v: 'incidente', grupo: 'seguranca', sst: true, icone: '📋', titulo: 'Incidente / condição perigosa', curto: 'Incidente / condição perigosa',
        sub: 'Condição ou ato inseguro, dano material', cor: '#2b6cb0', fundo: '#ebf8ff',
        phCirc: 'Local, horas, o que se estava a fazer, quem estava presente…',
        phDesc: 'Que condição ou ato inseguro foi observado, ou que dano houve…',
    },
];
const TIPO_INFO = Object.fromEntries(TIPOS.map(t => [t.v, t]));
const GRUPO_FORMS = TIPOS.filter(t => t.grupo === 'forms');
const GRUPO_SEGURANCA = TIPOS.filter(t => t.grupo === 'seguranca');

// Forms: DEPARTAMENTO / ÁREA
const DEPARTAMENTOS = [
    { v: 'comercial', l: 'Comercial' },
    { v: 'financeiro', l: 'Financeiro' },
    { v: 'rh', l: 'RH' },
    { v: 'implementacao', l: 'Implementação' },
];
const DEPARTAMENTO_LABEL = Object.fromEntries(DEPARTAMENTOS.map(d => [d.v, d.l]));

// Indicação da pergunta "Descrição" do Forms, com o texto completo
const DICA_CIRCUNSTANCIAS = '(para AT incluir testemunhas, local, horas, se teve tratamento hospitalar ou se foram acionados os meios de socorro)';

// Atalhos para "A executar em" (dias a partir de hoje)
const PRAZOS_RAPIDOS = [[0, 'Hoje'], [7, '+7 dias'], [15, '+15 dias'], [30, '+30 dias']];

const ESTADOS = {
    aberto: { l: 'Aberto', bg: '#e0f2fe', fg: '#075985' },
    em_investigacao: { l: 'Em investigação', bg: '#fef9c3', fg: '#854d0e' },
    fechado: { l: 'Fechado', bg: '#dcfce7', fg: '#166534' },
};

const PARTES_CORPO = ['Cabeça', 'Olhos', 'Mãos/dedos', 'Braços', 'Tronco', 'Costas', 'Pernas', 'Pés'];
const LESOES_SUGERIDAS = [
    'Corte / ferida', 'Contusão / pancada', 'Entorse', 'Fratura', 'Queimadura',
    'Corpo estranho no olho', 'Escoriação', 'Lesão muscular / lombalgia', 'Choque elétrico', 'Intoxicação',
];

// Nome de cada campo validado, para a lista de erros junto ao botão de enviar
const NOME_CAMPO = {
    tipo: 'Origem',
    data: 'Data',
    reportado_nome: 'Nome',
    departamento: 'Departamento / Área',
    trabalhador_id: 'Trabalhador',
    ext_nome: 'Nome da pessoa (externo)',
    circunstancias: 'Descrição',
    descricao: 'Descrição do incidente',
    plano_acao: 'Plano de ação e melhoria',
    plano_prazo: 'A executar em',
};
// Ao alterar um campo, apagam-se também os erros que dependem dele
const ERROS_LIGADOS = { quem: ['trabalhador_id', 'ext_nome'], data: ['plano_prazo'] };
const semErros = (erros, chaves) => {
    if (!chaves.some(k => k in erros)) return erros;
    const r = { ...erros };
    chaves.forEach(k => { delete r[k]; });
    return r;
};

// Leva o ecrã até ao campo (id "ri-campo-<chave>") e foca o primeiro campo de escrita que lá houver
const irParaCampo = (chave) => {
    const el = document.getElementById(`ri-campo-${chave}`);
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { el.scrollIntoView(); }
    const alvo = el.querySelector('input:not([type="file"]), select, textarea');
    if (alvo) {
        try { alvo.focus({ preventScroll: true }); } catch (_) { /* browser sem suporte — basta o scroll */ }
    }
};

// Sinistrado / envolvido a null (tipos de comunicação: a secção não existe)
const SEM_PESSOA = { trabalhador_id: null, nome: null, funcao: null, empregador: null, vinculo: null };

// Datas em hora local (o input date devolve 'YYYY-MM-DD' local)
const pad = (n) => String(n).padStart(2, '0');
const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hojeISO = () => isoLocal(new Date());
const haUmAnoISO = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return isoLocal(d); };
const daquiADiasISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return isoLocal(d); };
const limitePrazoISO = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 5); return isoLocal(d); };
const horaAgora = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
// 'YYYY-MM-DD' de um dia que existe (o input date do iOS não aplica min/max)
const dataValida = (iso) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return false;
    const [a, m, d] = iso.split('-').map(Number);
    const dt = new Date(a, m - 1, d);
    return dt.getFullYear() === a && dt.getMonth() === m - 1 && dt.getDate() === d;
};
const fmtData = (iso) => {
    if (!iso) return '—';
    const [a, m, d] = String(iso).substring(0, 10).split('-');
    return `${d}/${m}/${a}`;
};
const obraLabel = (o) => `${o.posicao}${o.descricao ? ` — ${o.descricao}` : ''}`;

const formVazio = (nome = '') => ({
    tipo: '',
    data: hojeISO(),
    hora: horaAgora(),
    reportado_nome: nome,  // Forms: NOME — quem comunica (pré-preenchido com o utilizador, editável)
    departamento: '',      // Forms: DEPARTAMENTO / ÁREA — 'comercial' | 'financeiro' | 'rh' | 'implementacao'
    obra_id: '',
    projeto_ref: '',       // Forms: Projeto (ID)
    local: '',
    quem: '',              // 'eu' | 'outro' | 'externo' | 'ninguem' (só tipos SST)
    trabalhador_id: '',
    ext_nome: '',
    ext_empresa: '',
    ext_funcao: '',
    sexo: '',              // 'M' | 'F' | ''
    circunstancias: '',    // Forms: Descrição (para AT incluir testemunhas, local, horas…)
    descricao: '',         // Forms: Descrição do Incidente
    lesao: '',
    partes: [],            // chips da parte do corpo
    lesao_outro: '',
    primeiros_socorros: null,
    assistencia_medica: null,
    meios_socorro: null,   // 112 / INEM (acidente e quase-acidente)
    danos_materiais: '',
    plano_acao: '',        // Forms: Plano de ação e Melhoria
    plano_prazo: '',       // Forms: A executar em
    testemunhas: [],       // [{ sel: '' | 't:<id>' | 'livre', nome }]
});

// Redimensionar/comprimir no cliente (máx. 1280px, JPEG q0.7), como na Ficha de Manutenção.
// Devolve null se o browser não conseguir ler a imagem (o servidor só aceita jpeg/png/webp).
const comprimirFoto = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const maxDim = 1280;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
            const escala = maxDim / Math.max(width, height);
            width = Math.round(width * escala);
            height = Math.round(height * escala);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        const dados = canvas.toDataURL('image/jpeg', 0.7);
        resolve(dados.startsWith('data:image/jpeg') ? dados : null);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

// Pergunta Sim/Não: true | false | null (tocar de novo na opção escolhida limpa a resposta)
const SimNao = ({ valor, onChange }) => (
    <div style={{ display: 'flex', gap: 8 }}>
        {[[true, 'Sim'], [false, 'Não']].map(([v, l]) => (
            <button key={l} type="button" aria-pressed={valor === v}
                onClick={() => onChange(valor === v ? null : v)}
                style={{ ...chip(valor === v), flex: 1 }}>
                {l}
            </button>
        ))}
    </div>
);

// Mensagem de erro por baixo de um campo
const MsgErro = ({ chave, erros }) => (erros[chave]
    ? <div id={`ri-erro-${chave}`} style={erroCampo}>⚠️ {erros[chave]}</div>
    : null);

const RegistoIncidente = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const nomeUtilizador = user?.nome_completo || user?.username || '';

    const [tab, setTab] = useState('novo');             // 'novo' | 'meus'
    const [opcoes, setOpcoes] = useState(null);         // { eu, trabalhadores, obras } — null = a carregar
    const [aviso, setAviso] = useState('');             // falha ao carregar as listas
    const [form, setForm] = useState(() => formVazio(nomeUtilizador));
    const [fotos, setFotos] = useState([]);             // data URLs já comprimidos
    const [aPrepararFotos, setAPrepararFotos] = useState(false);
    const [erros, setErros] = useState({});             // validação no cliente: { campo: mensagem }, pela ordem do ecrã
    const [irParaErro, setIrParaErro] = useState(null); // { chave } — levar o ecrã ao 1.º erro depois de renderizar
    const [erro, setErro] = useState('');               // envio / avisos gerais (junto ao botão)
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null);   // { id, numero, tipo, aviso_email }
    const [meus, setMeus] = useState(null);             // null = por carregar
    const [aCarregarMeus, setACarregarMeus] = useState(false);
    const [erroMeus, setErroMeus] = useState('');

    const cameraRef = useRef(null);
    const galeriaRef = useRef(null);

    const carregarOpcoes = useCallback(async () => {
        try {
            // _t: cache-buster — o Safari iOS reutilizava respostas GET antigas na PWA
            const r = await api.get('/incidentes-reporte/opcoes', { params: { _t: Date.now() } });
            setOpcoes({
                eu: r.data?.eu || null,
                trabalhadores: r.data?.trabalhadores || [],
                obras: r.data?.obras || [],
            });
            setAviso('');
        } catch (err) {
            console.error(err);
            setOpcoes({ eu: null, trabalhadores: [], obras: [] });
            setAviso('Não foi possível carregar as listas de obras e trabalhadores.');
        }
    }, []);

    const carregarMeus = useCallback(async () => {
        setACarregarMeus(true);
        try {
            const r = await api.get('/incidentes-reporte/meus', { params: { _t: Date.now() } });
            setMeus(Array.isArray(r.data) ? r.data : []);
            setErroMeus('');
        } catch (err) {
            console.error(err);
            setMeus(m => m || []);
            setErroMeus('Não foi possível carregar os seus registos.');
        } finally {
            setACarregarMeus(false);
        }
    }, []);

    useEffect(() => { carregarOpcoes(); carregarMeus(); }, [carregarOpcoes, carregarMeus]);

    // Depois de as mensagens de erro aparecerem, levar o ecrã ao primeiro campo por preencher
    useEffect(() => {
        if (irParaErro) irParaCampo(irParaErro.chave);
    }, [irParaErro]);

    // Voltar a pedir as listas sem sair da página (o que foi preenchido mantém-se)
    const recarregarOpcoes = () => { setAviso(''); setOpcoes(null); carregarOpcoes(); };

    const eu = opcoes?.eu || null;
    const trabalhadores = opcoes?.trabalhadores || [];
    const info = TIPO_INFO[form.tipo] || null;
    const sst = !!info?.sst;                 // acidente, quase-acidente, incidente: há pessoa envolvida
    const ehAcidente = form.tipo === 'acidente';
    const comSocorro = form.tipo === 'acidente' || form.tipo === 'quase_acidente';
    const fotosCheias = fotos.length >= MAX_FOTOS;
    const porEnviar = !resultado && (!!form.tipo || !!form.circunstancias.trim() || !!form.descricao.trim()
        || !!form.plano_acao.trim() || fotos.length > 0);

    // Alterar um campo apaga o erro dele (e os que dependem dele)
    const upd = (patch) => {
        setForm(f => ({ ...f, ...patch }));
        setErros(e => semErros(e, Object.keys(patch).flatMap(k => [k, ...(ERROS_LIGADOS[k] || [])])));
    };
    const campo = (k) => (e) => upd({ [k]: e.target.value });

    // Campo com erro: contorno vermelho e ligação à mensagem (leitores de ecrã)
    const inpE = (k) => (erros[k] ? { ...inp, ...inpComErro } : inp);
    const ariaErro = (k) => (erros[k] ? { 'aria-invalid': true, 'aria-describedby': `ri-erro-${k}` } : {});
    const nomeCampo = (k) => (k === 'quem' ? (ehAcidente ? 'Quem se magoou' : 'Quem esteve envolvido') : (NOME_CAMPO[k] || k));

    const escolherTipo = (tipo) => {
        setErro('');
        // Os erros da secção "quem" dependem do tipo (e a secção não existe nas comunicações)
        setErros(e => semErros(e, ['tipo', 'quem', 'trabalhador_id', 'ext_nome']));
        // "Ninguém" não se aplica a um acidente (tem de haver sinistrado)
        setForm(f => ({ ...f, tipo, quem: tipo === 'acidente' && f.quem === 'ninguem' ? '' : f.quem }));
    };

    const toggleParte = (p) => setForm(f => ({
        ...f,
        partes: f.partes.includes(p) ? f.partes.filter(x => x !== p) : [...f.partes, p],
    }));

    const setTestemunha = (i, patch) => setForm(f => ({
        ...f,
        testemunhas: f.testemunhas.map((t, x) => (x === i ? { ...t, ...patch } : t)),
    }));
    const juntarTestemunha = () => setForm(f => (
        f.testemunhas.length >= MAX_TESTEMUNHAS ? f : { ...f, testemunhas: [...f.testemunhas, { sel: '', nome: '' }] }
    ));
    const removerTestemunha = (i) => setForm(f => ({ ...f, testemunhas: f.testemunhas.filter((_, x) => x !== i) }));

    const juntarFotos = async (e) => {
        const input = e.target;
        const ficheiros = Array.from(input.files || []);
        if (!ficheiros.length) return;
        const livres = MAX_FOTOS - fotos.length;
        if (livres <= 0) {
            input.value = '';
            alert(`Máximo de ${MAX_FOTOS} fotos por registo.`);
            return;
        }
        if (ficheiros.length > livres) alert(`Só pode juntar mais ${livres} foto(s) — máximo de ${MAX_FOTOS}.`);
        setAPrepararFotos(true);
        try {
            const aLer = ficheiros.slice(0, livres);
            const novas = [];
            for (const f of aLer) {
                const dados = await comprimirFoto(f);
                if (dados) novas.push(dados);
            }
            if (novas.length < aLer.length) setErro('Alguma foto não pôde ser lida — tente tirá-la de novo.');
            setFotos(prev => [...prev, ...novas].slice(0, MAX_FOTOS));
        } finally {
            setAPrepararFotos(false);
            input.value = '';   // permite voltar a escolher o mesmo ficheiro
        }
    };
    const removerFoto = (i) => setFotos(prev => prev.filter((_, x) => x !== i));

    // Validação no cliente. Devolve { campo: mensagem } pela ordem em que os campos aparecem no ecrã.
    const validar = () => {
        const e = {};
        if (!form.tipo) e.tipo = 'Escolha a origem da comunicação.';
        if (!form.data) e.data = 'Indique a data.';
        else if (!dataValida(form.data)) e.data = 'Data inválida.';
        else if (form.data > hojeISO()) e.data = 'A data não pode ser no futuro.';
        else if (form.data < haUmAnoISO()) e.data = 'A data não pode ser anterior a um ano.';
        if (!form.reportado_nome.trim()) e.reportado_nome = 'Escreva o nome de quem comunica.';
        if (!form.departamento) e.departamento = 'Escolha o departamento / área.';
        if (sst) {
            if (!form.quem) e.quem = ehAcidente ? 'Indique quem se magoou.' : 'Indique quem esteve envolvido (ou "Ninguém em particular").';
            else if (form.quem === 'eu' && !eu) e.quem = 'A sua conta não está ligada a uma ficha de trabalhador — escolha outra opção.';
            else if (form.quem === 'outro' && !form.trabalhador_id) e.trabalhador_id = 'Escolha o trabalhador na lista.';
            else if (form.quem === 'externo' && !form.ext_nome.trim()) e.ext_nome = 'Escreva o nome da pessoa (subcontratado / externo).';
        }
        if (!form.circunstancias.trim()) e.circunstancias = 'Preencha a descrição (circunstâncias).';
        if (!form.descricao.trim()) e.descricao = 'Preencha a descrição do incidente.';
        if (!form.plano_acao.trim()) e.plano_acao = 'Indique o plano de ação e melhoria.';
        if (!form.plano_prazo) e.plano_prazo = 'Indique quando vai ser executado o plano de ação.';
        else if (!dataValida(form.plano_prazo)) e.plano_prazo = 'Data inválida.';
        else if (dataValida(form.data) && form.plano_prazo < form.data) e.plano_prazo = 'Não pode ser anterior à data indicada em cima.';
        else if (form.plano_prazo > limitePrazoISO()) e.plano_prazo = 'Data demasiado distante — confirme o ano.';
        return e;
    };

    // Sinistrado / envolvido. Com trabalhador_id o servidor preenche nome, função e empregador a partir da ficha.
    const pessoa = () => {
        if (form.quem === 'eu' && eu) {
            return { trabalhador_id: eu.trabalhador_id, nome: eu.nome, funcao: eu.funcao || null, empregador: eu.empresa || 'SPROD', vinculo: 'interno' };
        }
        if (form.quem === 'outro') {
            const t = trabalhadores.find(x => String(x.id) === String(form.trabalhador_id));
            return { trabalhador_id: Number(form.trabalhador_id), nome: t?.nome || null, funcao: t?.funcao || null, empregador: null, vinculo: 'interno' };
        }
        if (form.quem === 'externo') {
            return { trabalhador_id: null, nome: form.ext_nome.trim(), funcao: form.ext_funcao.trim() || null, empregador: form.ext_empresa.trim() || null, vinculo: 'subcontratado' };
        }
        return { trabalhador_id: null, nome: null, funcao: null, empregador: null, vinculo: 'interno' };
    };

    const listaTestemunhas = () => form.testemunhas.map(t => {
        if (t.sel === 'livre') return { trabalhador_id: null, nome: t.nome.trim(), funcao: '' };
        if (t.sel.startsWith('t:')) {
            const w = trabalhadores.find(x => String(x.id) === t.sel.slice(2));
            return w ? { trabalhador_id: w.id, nome: w.nome, funcao: w.funcao || '' } : null;
        }
        return null;
    }).filter(t => t && t.nome);

    // Corpo do POST. Os campos das secções que o tipo escolhido não mostra vão a null, para não
    // ficar lixo de um tipo escolhido antes (ex.: a lesão de um "acidente" que passou a "reclamação").
    const dadosEnvio = () => {
        const localLesao = [...form.partes, form.lesao_outro.trim()].filter(Boolean).join(', ');
        const temPessoa = sst && form.quem !== 'ninguem';
        return {
            tipo: form.tipo,
            data_ocorrencia: form.data,
            hora: sst ? (form.hora || null) : null,
            reportado_nome: form.reportado_nome.trim(),
            departamento: form.departamento,
            obra_id: form.obra_id ? Number(form.obra_id) : null,
            projeto_ref: form.projeto_ref.trim() || null,
            local: form.local.trim() || null,
            ...(sst ? pessoa() : SEM_PESSOA),
            sexo: temPessoa ? (form.sexo || null) : null,
            circunstancias: form.circunstancias.trim(),
            descricao: form.descricao.trim(),
            lesao: ehAcidente ? (form.lesao.trim() || null) : null,
            local_lesao: ehAcidente ? (localLesao || null) : null,
            primeiros_socorros: ehAcidente ? form.primeiros_socorros : null,
            assistencia_medica: ehAcidente ? form.assistencia_medica : null,
            meios_socorro: comSocorro ? form.meios_socorro : null,
            danos_materiais: form.tipo === 'incidente' ? (form.danos_materiais.trim() || null) : null,
            plano_acao: form.plano_acao.trim(),
            plano_prazo: form.plano_prazo,
            testemunhas_lista: listaTestemunhas(),
            fotos_base64: fotos,
            origem_url: window.location.origin, // base do link no email ao RSI
        };
    };

    const enviar = async (e) => {
        e.preventDefault();
        if (enviando) return;
        if (aPrepararFotos) { setErro('Aguarde — as fotos ainda estão a ser preparadas.'); return; }
        const problemas = validar();
        const chaves = Object.keys(problemas);
        setErros(problemas);
        if (chaves.length) {
            setErro('');
            setIrParaErro({ chave: chaves[0] });
            return;
        }
        setEnviando(true); setErro('');
        try {
            const r = await api.post('/incidentes-reporte', dadosEnvio(), { timeout: 60000 });
            setResultado({ ...(r.data || {}), tipo: r.data?.tipo || form.tipo });
            carregarMeus();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error(err);
            const resp = err.response;
            if (resp?.data?.error) {
                setErro(resp.data.error);                       // a API respondeu com erro: nada ficou gravado
            } else if (!resp || resp.status >= 502) {
                // Timeout (ECONNABORTED), rede caiu (ERR_NETWORK) ou erro de gateway: o servidor pode ter
                // gravado e numerado o registo antes de a resposta chegar — não convidar a reenviar às cegas
                // (duplicaria o número, o email ao RSI e as contagens do Mapa e dos índices).
                carregarMeus();
                setErro('Não recebemos a confirmação do servidor — o registo pode ter ficado gravado. Veja em "Os meus registos" antes de voltar a enviar (o que preencheu mantém-se aqui).');
            } else {
                setErro('Não foi possível enviar o registo. Verifique a ligação e tente novamente.');
            }
        } finally {
            setEnviando(false);
        }
    };

    const limpar = () => {
        setForm(formVazio(nomeUtilizador));
        setFotos([]);
        setResultado(null);
        setErro('');
        setErros({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const irPara = (t) => {
        if (resultado) limpar();
        setTab(t);
        if (t === 'meus') carregarMeus();
    };

    const sair = (destino) => {
        if (porEnviar && !window.confirm('O registo ainda não foi enviado. Sair e perder o que preencheu?')) return;
        navigate(destino);
    };

    // Enter num campo de texto não submete (evita envios acidentais com a tecla "Ir" do telemóvel)
    const bloquearEnter = (e) => {
        if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault();
    };

    const botaoTipo = (t) => {
        const ativo = form.tipo === t.v;
        return (
            <button key={t.v} type="button" aria-pressed={ativo} onClick={() => escolherTipo(t.v)} style={tipoBtn(ativo, t)}>
                <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden="true">{t.icone}</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25 }}>{ativo ? '✓ ' : ''}{t.titulo}</span>
                <span style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.3, opacity: 0.85 }}>{t.sub}</span>
            </button>
        );
    };

    const nErros = Object.keys(erros).length;

    return (
        <div className="verificacao-andaimes-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>🚑 Incidentes / Comunicações</h1>
                        <p className="mobile-hide">Acidentes, incidentes, reclamações, não conformidades e sugestões — comunicação ao RSI</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    {user?.perfil_mobile ? (
                        <button className="btn-secondary" style={{ minHeight: 44, whiteSpace: 'nowrap' }} onClick={() => sair('/mobile-dashboard')}>← Menu</button>
                    ) : (
                        <button className="btn-secondary" style={{ minHeight: 44, whiteSpace: 'nowrap' }} onClick={() => sair(-1)}>← Voltar</button>
                    )}
                </div>
            </div>

            <div className="page-content" style={{ padding: 16 }}>
                <div className="va-content-wrapper">
                    <div style={tabsBox}>
                        <button type="button" style={tabBtn(tab === 'novo')} onClick={() => irPara('novo')}>➕ Novo registo</button>
                        <button type="button" style={tabBtn(tab === 'meus')} onClick={() => irPara('meus')}>
                            📋 Os meus registos{meus?.length ? ` (${meus.length})` : ''}
                        </button>
                    </div>

                    {tab === 'meus' ? (
                        /* ---------- OS MEUS REGISTOS ---------- */
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <span style={{ color: '#718096', fontSize: 13 }}>Últimos 12 meses</span>
                                <button type="button" onClick={carregarMeus} disabled={aCarregarMeus} style={btnMini}>
                                    {aCarregarMeus ? 'A atualizar…' : '🔄 Atualizar'}
                                </button>
                            </div>
                            {erroMeus && <div className="va-message error">{erroMeus}</div>}
                            {meus === null ? (
                                <div style={vazio}>A carregar…</div>
                            ) : meus.length === 0 ? (
                                <div style={vazio}>Ainda não fez nenhum registo nos últimos 12 meses.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {meus.map(r => {
                                        const t = TIPO_INFO[r.tipo];
                                        const est = ESTADOS[r.estado] || ESTADOS.aberto;
                                        const dep = DEPARTAMENTO_LABEL[r.departamento] || '';
                                        const onde = [r.obra_nome, r.projeto_ref ? `Projeto ${r.projeto_ref}` : '', r.local].filter(Boolean).join(' — ');
                                        return (
                                            <div key={r.id} style={{ ...cartao, marginBottom: 0, borderLeft: `4px solid ${t?.cor || '#a0aec0'}` }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <strong style={{ fontSize: 15 }}>{r.numero || '—'}</strong>
                                                    <span style={{ ...badge(t?.fundo || '#f1f5f9', t?.cor || '#4a5568'), whiteSpace: 'normal' }}>
                                                        {t ? `${t.icone} ${t.curto}` : r.tipo}
                                                    </span>
                                                    <span style={{ ...badge(est.bg, est.fg), marginLeft: 'auto' }}>{est.l}</span>
                                                </div>
                                                <div style={{ fontSize: 13, color: '#4a5568', marginTop: 6, lineHeight: 1.5 }}>
                                                    📅 {fmtData(r.data_ocorrencia)}{dep ? ` · 🏢 ${dep}` : ''}{onde ? ` · 📍 ${onde}` : ''}
                                                </div>
                                                {r.descricao && (
                                                    <div style={{ fontSize: 14, color: '#2d3748', marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {r.descricao}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ) : resultado ? (
                        /* ---------- SUCESSO ---------- */
                        <div className="va-success-actions">
                            <div style={{ fontSize: 44, lineHeight: 1 }}>✅</div>
                            <p className="success-text" style={{ margin: '12px 0 6px' }}>Registo enviado</p>
                            <div style={{ fontSize: 26, fontWeight: 800, color: '#1a202c', letterSpacing: 0.5 }}>{resultado.numero || '—'}</div>
                            <p style={{ color: '#4a5568', margin: '8px 0 16px' }}>
                                {TIPO_INFO[resultado.tipo] ? `${TIPO_INFO[resultado.tipo].curto} — ` : ''}o registo chegou ao Sistema Integrado e vai ser analisado pelo RSI.
                            </p>
                            {resultado.tipo === 'acidente' && (
                                <div role="alert" style={avisoVermelho}>
                                    ⚠️ Informe de imediato a chefia/RSI — o acidente de trabalho tem de ser comunicado à seguradora em 24 h.
                                </div>
                            )}
                            {typeof resultado.aviso_email === 'string' && resultado.aviso_email && (
                                <p style={{ fontSize: 13, color: '#718096', margin: '0 0 12px' }}>✉️ {resultado.aviso_email}</p>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <button type="button" className="va-action-btn" onClick={() => irPara('novo')}
                                    style={{ background: '#0056b3', color: '#fff', width: '100%', padding: '14px 20px' }}>
                                    ➕ Novo registo
                                </button>
                                <button type="button" className="va-action-btn secondary-btn" onClick={() => irPara('meus')}
                                    style={{ width: '100%', padding: '14px 20px' }}>
                                    📋 Os meus registos
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ---------- NOVO REGISTO ---------- */
                        <form className="va-form" onSubmit={enviar} onKeyDown={bloquearEnter} noValidate>
                            {aviso && (
                                <div className="va-message error" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ flex: 1, minWidth: 180 }}>{aviso}</span>
                                    <button type="button" style={btnMini} onClick={recarregarOpcoes}>🔄 Tentar novamente</button>
                                </div>
                            )}

                            {/* Forms: ORIGEM */}
                            <div id="ri-campo-tipo" role="group" aria-labelledby="ri-tit-tipo" style={cartaoE(!!erros.tipo)}>
                                <div id="ri-tit-tipo" style={secTit}>Origem *</div>
                                <div style={grelha2}>{GRUPO_FORMS.map(botaoTipo)}</div>
                                <div style={subTitGrupo}>Segurança (SST)</div>
                                <div style={grelha2}>{GRUPO_SEGURANCA.map(botaoTipo)}</div>
                                <MsgErro chave="tipo" erros={erros} />
                                {ehAcidente && (
                                    <div style={{ ...avisoVermelho, marginTop: 12, marginBottom: 0 }}>
                                        🆘 Lesão grave? Ligue primeiro o 112 — o registo pode ser feito depois.
                                    </div>
                                )}
                            </div>

                            {info && (<>
                                {/* Forms: DATA, NOME, DEPARTAMENTO / ÁREA */}
                                <div style={cartao}>
                                    <div style={secTit}>Quando e quem comunica</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: sst ? 'minmax(0, 1.3fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 10 }}>
                                        <div id="ri-campo-data" className="va-form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                                            <label htmlFor="ri-data">Data *</label>
                                            <input id="ri-data" type="date" value={form.data} min={haUmAnoISO()} max={hojeISO()}
                                                onChange={campo('data')} style={inpE('data')} {...ariaErro('data')} />
                                        </div>
                                        {sst && (
                                            <div className="va-form-group" style={{ marginBottom: 0, minWidth: 0 }}>
                                                <label htmlFor="ri-hora">Hora</label>
                                                <input id="ri-hora" type="time" value={form.hora} onChange={campo('hora')} style={inp} />
                                            </div>
                                        )}
                                    </div>
                                    <MsgErro chave="data" erros={erros} />

                                    <div id="ri-campo-reportado_nome" className="va-form-group" style={{ marginTop: 14 }}>
                                        <label htmlFor="ri-nome">Nome *</label>
                                        <input id="ri-nome" type="text" value={form.reportado_nome} onChange={campo('reportado_nome')} maxLength={200}
                                            autoComplete="name" placeholder="Nome de quem comunica" style={inpE('reportado_nome')} {...ariaErro('reportado_nome')} />
                                        <div style={dica}>Quem comunica — altere se estiver a registar em nome de outra pessoa.</div>
                                        <MsgErro chave="reportado_nome" erros={erros} />
                                    </div>

                                    <div id="ri-campo-departamento">
                                        <div id="ri-rot-departamento" style={rotulo}>Departamento / Área *</div>
                                        <div role="group" aria-labelledby="ri-rot-departamento" style={grelha2}>
                                            {DEPARTAMENTOS.map(d => {
                                                const ativo = form.departamento === d.v;
                                                return (
                                                    <button key={d.v} type="button" aria-pressed={ativo} onClick={() => upd({ departamento: d.v })}
                                                        style={opcaoGrelha(ativo, !!erros.departamento)}>
                                                        {ativo ? '✓ ' : ''}{d.l}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <MsgErro chave="departamento" erros={erros} />
                                    </div>
                                </div>

                                {/* Forms: Projeto (ID) — mais a obra e o local */}
                                <div style={cartao}>
                                    <div style={secTit}>Obra / projeto e local</div>
                                    <div className="va-form-group">
                                        <label htmlFor="ri-obra">Obra</label>
                                        <select id="ri-obra" value={form.obra_id} onChange={campo('obra_id')} style={inp}>
                                            <option value="">{opcoes ? '— Sem obra / outro local —' : 'A carregar…'}</option>
                                            {(opcoes?.obras || []).map(o => <option key={o.id} value={o.id}>{obraLabel(o)}</option>)}
                                        </select>
                                    </div>
                                    <div className="va-form-group">
                                        <label htmlFor="ri-projeto">Projeto (ID)</label>
                                        <input id="ri-projeto" type="text" value={form.projeto_ref} onChange={campo('projeto_ref')} maxLength={100}
                                            autoCapitalize="none" autoCorrect="off" spellCheck={false}
                                            placeholder="Identificação do projeto, se houver" style={inp} />
                                    </div>
                                    <div className="va-form-group" style={{ marginBottom: 0 }}>
                                        <label htmlFor="ri-local">Local exato</label>
                                        <input id="ri-local" type="text" value={form.local} onChange={campo('local')} maxLength={200}
                                            placeholder="Ex.: piso 2, escadas, estaleiro, armazém…" style={inp} />
                                    </div>
                                </div>

                                {/* Sinistrado / envolvido — só nos tipos SST */}
                                {sst && (
                                    <div id="ri-campo-quem" style={cartaoE(!!erros.quem)}>
                                        <div style={secTit}>{ehAcidente ? 'Quem se magoou? *' : 'Quem esteve envolvido? *'}</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {eu && (
                                                <button type="button" aria-pressed={form.quem === 'eu'} onClick={() => upd({ quem: 'eu' })} style={opcaoBtn(form.quem === 'eu')}>
                                                    <span style={{ fontSize: 15, fontWeight: 600 }}>🙋 Eu próprio</span>
                                                    <span style={opcaoSub}>{eu.nome}{eu.funcao ? ` · ${eu.funcao}` : ''}</span>
                                                </button>
                                            )}
                                            <button type="button" aria-pressed={form.quem === 'outro'} onClick={() => upd({ quem: 'outro' })} style={opcaoBtn(form.quem === 'outro')}>
                                                <span style={{ fontSize: 15, fontWeight: 600 }}>👷 Outro trabalhador</span>
                                            </button>
                                            <button type="button" aria-pressed={form.quem === 'externo'} onClick={() => upd({ quem: 'externo' })} style={opcaoBtn(form.quem === 'externo')}>
                                                <span style={{ fontSize: 15, fontWeight: 600 }}>🏢 Subcontratado / externo</span>
                                            </button>
                                            {!ehAcidente && (
                                                <button type="button" aria-pressed={form.quem === 'ninguem'} onClick={() => upd({ quem: 'ninguem' })} style={opcaoBtn(form.quem === 'ninguem')}>
                                                    <span style={{ fontSize: 15, fontWeight: 600 }}>🚫 Ninguém em particular</span>
                                                    <span style={opcaoSub}>Só a situação / o dano</span>
                                                </button>
                                            )}
                                        </div>
                                        <MsgErro chave="quem" erros={erros} />

                                        {form.quem === 'outro' && (
                                            <div id="ri-campo-trabalhador_id" className="va-form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                                                <label htmlFor="ri-trabalhador">Trabalhador *</label>
                                                <select id="ri-trabalhador" value={form.trabalhador_id} onChange={campo('trabalhador_id')}
                                                    style={inpE('trabalhador_id')} {...ariaErro('trabalhador_id')}>
                                                    <option value="">{opcoes ? '— Escolher trabalhador —' : 'A carregar…'}</option>
                                                    {trabalhadores.map(t => (
                                                        <option key={t.id} value={t.id}>{t.nome}{t.funcao ? ` — ${t.funcao}` : ''}</option>
                                                    ))}
                                                </select>
                                                <MsgErro chave="trabalhador_id" erros={erros} />
                                                {opcoes && !trabalhadores.length && (
                                                    // O aviso do topo pode já estar fora do ecrã: repetir aqui, junto da lista vazia
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                                                        <span style={{ flex: 1, minWidth: 160, fontSize: 13, color: '#9b2c2c' }}>Lista de trabalhadores indisponível.</span>
                                                        <button type="button" style={btnMini} onClick={recarregarOpcoes}>🔄 Tentar novamente</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {form.quem === 'externo' && (
                                            <div style={{ marginTop: 12 }}>
                                                <div id="ri-campo-ext_nome" className="va-form-group">
                                                    <label htmlFor="ri-ext-nome">Nome da pessoa *</label>
                                                    <input id="ri-ext-nome" type="text" value={form.ext_nome} onChange={campo('ext_nome')} maxLength={150}
                                                        placeholder="Nome da pessoa" style={inpE('ext_nome')} {...ariaErro('ext_nome')} />
                                                    <MsgErro chave="ext_nome" erros={erros} />
                                                </div>
                                                <div className="va-form-group">
                                                    <label htmlFor="ri-ext-empresa">Empresa</label>
                                                    <input id="ri-ext-empresa" type="text" value={form.ext_empresa} onChange={campo('ext_empresa')} maxLength={150}
                                                        placeholder="Empresa subcontratada / entidade" style={inp} />
                                                </div>
                                                <div className="va-form-group" style={{ marginBottom: 0 }}>
                                                    <label htmlFor="ri-ext-funcao">Função</label>
                                                    <input id="ri-ext-funcao" type="text" value={form.ext_funcao} onChange={campo('ext_funcao')} maxLength={100}
                                                        placeholder="Ex.: eletricista, servente…" style={inp} />
                                                </div>
                                            </div>
                                        )}

                                        {['eu', 'outro', 'externo'].includes(form.quem) && (
                                            <div className="va-form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                                                <label>Sexo</label>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    {[['M', 'Masculino'], ['F', 'Feminino']].map(([v, l]) => (
                                                        <button key={v} type="button" aria-pressed={form.sexo === v}
                                                            onClick={() => upd({ sexo: form.sexo === v ? '' : v })}
                                                            style={{ ...chip(form.sexo === v), flex: 1 }}>
                                                            {l}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Forms: Descrição (circunstâncias) */}
                                <div id="ri-campo-circunstancias" style={cartao}>
                                    <label htmlFor="ri-circunstancias" style={secTitRotulo}>
                                        Descrição *
                                        <span style={dicaTit}>{DICA_CIRCUNSTANCIAS}</span>
                                    </label>
                                    <textarea id="ri-circunstancias" rows={4} value={form.circunstancias} onChange={campo('circunstancias')} maxLength={4000}
                                        placeholder={info.phCirc} style={{ ...inpE('circunstancias'), minHeight: 110, resize: 'vertical' }}
                                        {...ariaErro('circunstancias')} />
                                    <MsgErro chave="circunstancias" erros={erros} />
                                </div>

                                {/* Forms: Descrição do Incidente */}
                                <div id="ri-campo-descricao" style={cartao}>
                                    <label htmlFor="ri-descricao" style={secTitRotulo}>Descrição do incidente *</label>
                                    <textarea id="ri-descricao" rows={5} value={form.descricao} onChange={campo('descricao')} maxLength={4000}
                                        placeholder={info.phDesc} style={{ ...inpE('descricao'), minHeight: 120, resize: 'vertical' }}
                                        {...ariaErro('descricao')} />
                                    <MsgErro chave="descricao" erros={erros} />
                                </div>

                                {ehAcidente && (
                                    <div style={cartao}>
                                        <div style={secTit}>Lesão</div>
                                        <div className="va-form-group">
                                            <label htmlFor="ri-lesao">Tipo de lesão</label>
                                            <input id="ri-lesao" type="text" list="ri-lesoes" value={form.lesao} onChange={campo('lesao')} maxLength={200}
                                                placeholder="Ex.: corte, entorse, queimadura…" style={inp} />
                                            <datalist id="ri-lesoes">
                                                {LESOES_SUGERIDAS.map(l => <option key={l} value={l} />)}
                                            </datalist>
                                        </div>
                                        <div className="va-form-group" style={{ marginBottom: 0 }}>
                                            <label>Parte do corpo atingida</label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {PARTES_CORPO.map(p => (
                                                    <button key={p} type="button" aria-pressed={form.partes.includes(p)}
                                                        onClick={() => toggleParte(p)} style={chip(form.partes.includes(p))}>
                                                        {p}
                                                    </button>
                                                ))}
                                            </div>
                                            <input type="text" value={form.lesao_outro} onChange={campo('lesao_outro')} maxLength={150}
                                                aria-label="Outra parte do corpo / pormenor"
                                                placeholder="Outra / pormenor (ex.: mão esquerda)" style={{ ...inp, marginTop: 10 }} />
                                        </div>
                                    </div>
                                )}

                                {comSocorro && (
                                    <div style={cartao}>
                                        <div style={secTit}>{ehAcidente ? 'Socorro e assistência' : 'Socorro'}</div>
                                        {ehAcidente && (<>
                                            <div className="va-form-group">
                                                <label>Recebeu primeiros socorros?</label>
                                                <SimNao valor={form.primeiros_socorros} onChange={v => upd({ primeiros_socorros: v })} />
                                            </div>
                                            <div className="va-form-group">
                                                <label>Assistência médica / tratamento hospitalar?</label>
                                                <SimNao valor={form.assistencia_medica} onChange={v => upd({ assistencia_medica: v })} />
                                            </div>
                                        </>)}
                                        <div className="va-form-group" style={{ marginBottom: 0 }}>
                                            <label>Foram acionados os meios de socorro (112 / INEM)?</label>
                                            <SimNao valor={form.meios_socorro} onChange={v => upd({ meios_socorro: v })} />
                                        </div>
                                    </div>
                                )}

                                {form.tipo === 'incidente' && (
                                    <div style={cartao}>
                                        <label htmlFor="ri-danos" style={secTitRotulo}>Danos materiais <span style={opcional}>(se houver)</span></label>
                                        <textarea id="ri-danos" rows={3} value={form.danos_materiais} onChange={campo('danos_materiais')} maxLength={2000}
                                            placeholder="Equipamento, veículo ou material danificado…"
                                            style={{ ...inp, minHeight: 80, resize: 'vertical' }} />
                                    </div>
                                )}

                                {/* Forms: Plano de ação e Melhoria + A executar em */}
                                <div style={cartao}>
                                    <div id="ri-campo-plano_acao">
                                        <label htmlFor="ri-plano" style={secTitRotulo}>Plano de ação e melhoria *</label>
                                        <textarea id="ri-plano" rows={4} value={form.plano_acao} onChange={campo('plano_acao')} maxLength={4000}
                                            placeholder="O que vai ser feito para corrigir a situação e evitar que se repita (e por quem)…"
                                            style={{ ...inpE('plano_acao'), minHeight: 110, resize: 'vertical' }} {...ariaErro('plano_acao')} />
                                        <MsgErro chave="plano_acao" erros={erros} />
                                    </div>
                                    <div id="ri-campo-plano_prazo" className="va-form-group" style={{ marginTop: 14, marginBottom: 0 }}>
                                        <label htmlFor="ri-prazo">A executar em *</label>
                                        <input id="ri-prazo" type="date" value={form.plano_prazo} min={dataValida(form.data) ? form.data : undefined}
                                            max={limitePrazoISO()} onChange={campo('plano_prazo')} style={inpE('plano_prazo')} {...ariaErro('plano_prazo')} />
                                        <div style={{ ...grelha2, gap: 8, marginTop: 8 }}>
                                            {PRAZOS_RAPIDOS.map(([dias, l]) => {
                                                const v = daquiADiasISO(dias);
                                                const ativo = form.plano_prazo === v;
                                                return (
                                                    <button key={dias} type="button" aria-pressed={ativo} onClick={() => upd({ plano_prazo: v })}
                                                        aria-label={dias ? `Daqui a ${dias} dias (${fmtData(v)})` : `Hoje (${fmtData(v)})`}
                                                        style={chip(ativo)}>
                                                        {l}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <MsgErro chave="plano_prazo" erros={erros} />
                                    </div>
                                </div>

                                <div style={cartao}>
                                    <div style={secTit}>Testemunhas <span style={opcional}>(opcional)</span></div>
                                    {form.testemunhas.map((t, i) => (
                                        <div key={i} style={linhaTestemunha}>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <select value={t.sel} onChange={e => setTestemunha(i, { sel: e.target.value })}
                                                    aria-label={`Testemunha ${i + 1}`} style={{ ...inp, flex: 1 }}>
                                                    <option value="">— Testemunha {i + 1} —</option>
                                                    {trabalhadores.map(w => <option key={w.id} value={`t:${w.id}`}>{w.nome}</option>)}
                                                    <option value="livre">✏️ Outra pessoa (escrever o nome)</option>
                                                </select>
                                                <button type="button" onClick={() => removerTestemunha(i)} style={btnRemover}
                                                    aria-label={`Remover testemunha ${i + 1}`}><DeleteIcon /></button>
                                            </div>
                                            {t.sel === 'livre' && (
                                                <input type="text" value={t.nome} onChange={e => setTestemunha(i, { nome: e.target.value })}
                                                    maxLength={150} placeholder="Nome da testemunha" aria-label={`Nome da testemunha ${i + 1}`}
                                                    style={{ ...inp, marginTop: 8 }} />
                                            )}
                                        </div>
                                    ))}
                                    {form.testemunhas.length < MAX_TESTEMUNHAS && (
                                        <button type="button" onClick={juntarTestemunha} style={btnSec}>➕ Adicionar testemunha</button>
                                    )}
                                </div>

                                <div style={cartao}>
                                    <div style={secTit}>Fotos <span style={opcional}>({fotos.length}/{MAX_FOTOS})</span></div>
                                    {/* Câmara: input próprio com capture e SEM multiple (o iOS ignora o capture com multiple) */}
                                    <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                                        onChange={juntarFotos} style={{ display: 'none' }} />
                                    <input ref={galeriaRef} type="file" accept="image/*" multiple
                                        onChange={juntarFotos} style={{ display: 'none' }} />
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button type="button" className="va-action-btn" disabled={fotosCheias || aPrepararFotos}
                                            onClick={() => cameraRef.current?.click()}
                                            style={{ ...btnFoto('#2b6cb0'), opacity: fotosCheias || aPrepararFotos ? 0.5 : 1 }}>
                                            📷 Câmara
                                        </button>
                                        <button type="button" className="va-action-btn" disabled={fotosCheias || aPrepararFotos}
                                            onClick={() => galeriaRef.current?.click()}
                                            style={{ ...btnFoto('#4a5568'), opacity: fotosCheias || aPrepararFotos ? 0.5 : 1 }}>
                                            🖼️ Galeria
                                        </button>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#718096', marginTop: 6 }}>
                                        {aPrepararFotos ? 'A preparar fotos…' : 'As fotos ficam reservadas ao Sistema Integrado.'}
                                    </div>
                                    {fotos.length > 0 && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
                                            {fotos.map((f, i) => (
                                                <div key={i} style={{ position: 'relative' }}>
                                                    <img src={f} alt={`Foto ${i + 1}`}
                                                        style={{ display: 'block', width: '100%', height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #cbd5e0' }} />
                                                    <button type="button" onClick={() => removerFoto(i)} style={btnRemoverFoto}
                                                        aria-label={`Remover foto ${i + 1}`}><DeleteIcon /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {nErros > 0 && (
                                    <div className="va-message error" role="alert">
                                        <div style={{ marginBottom: 8 }}>
                                            {nErros === 1 ? 'Falta preencher ou corrigir 1 campo' : `Faltam preencher ou corrigir ${nErros} campos`} — toque para ir ao campo:
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {Object.keys(erros).map(k => (
                                                <button key={k} type="button" onClick={() => irParaCampo(k)} style={btnIrErro}>
                                                    {nomeCampo(k)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {erro && <div className="va-message error" role="alert">{erro}</div>}

                                <div className="va-actions" style={{ marginTop: 6 }}>
                                    <button type="submit" className="va-submit-btn" disabled={enviando || aPrepararFotos}
                                        style={{ padding: '16px 20px', width: '100%' }}>
                                        {enviando ? 'A enviar…' : '📨 Enviar registo'}
                                    </button>
                                </div>
                            </>)}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegistoIncidente;

/* ---------- estilos ---------- */
const cartao = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
// Cartão de um grupo de botões com erro (origem, quem)
const cartaoE = (comErro) => (comErro ? { ...cartao, border: '1px solid #fc8181', boxShadow: '0 0 0 1px #fc8181' } : cartao);
const secTit = { fontWeight: 700, fontSize: 15, color: '#1a202c', marginBottom: 10 };
const secTitRotulo = { ...secTit, display: 'block' };  // título do cartão que é o rótulo do próprio campo
const dicaTit = { display: 'block', fontWeight: 400, fontSize: 13, color: '#4a5568', lineHeight: 1.4, marginTop: 3 };
const dica = { fontSize: 12, color: '#718096', marginTop: 6, lineHeight: 1.35 };
const rotulo = { fontWeight: 600, marginBottom: 8, fontSize: '0.95rem', color: '#444' };  // igual a .va-form-group label
const subTitGrupo = { fontSize: 12, fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: 0.6, margin: '16px 0 8px' };
const opcional = { fontWeight: 400, fontSize: 13, color: '#718096' };
const grelha2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 };
// fontSize 16: abaixo disso o Safari do iPhone faz zoom ao tocar no campo
const inp = {
    display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', minHeight: 44, fontSize: 16,
    padding: '10px 12px', border: '1px solid #cbd5e0', borderRadius: 8, background: '#fff', color: '#2d3748', fontFamily: 'inherit',
};
const inpComErro = { border: '1px solid #e53e3e', boxShadow: '0 0 0 1px #e53e3e' };
const erroCampo = { color: '#c53030', fontSize: 13, fontWeight: 600, marginTop: 6, lineHeight: 1.35 };
const chip = (ativo) => ({
    minHeight: 44, padding: '9px 14px', borderRadius: 22, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
    border: '1px solid ' + (ativo ? '#2b6cb0' : '#cbd5e0'),
    background: ativo ? '#2b6cb0' : '#fff', color: ativo ? '#fff' : '#334155',
});
const tipoBtn = (ativo, t) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, width: '100%', minWidth: 0, minHeight: 104,
    textAlign: 'left', overflowWrap: 'break-word', wordBreak: 'break-word',
    padding: '12px 10px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
    border: `2px solid ${ativo ? t.cor : '#e2e8f0'}`, background: ativo ? t.fundo : '#fff',
    color: ativo ? t.cor : '#2d3748', boxShadow: ativo ? `0 0 0 3px ${t.cor}22` : 'none',
});
const opcaoGrelha = (ativo, comErro) => ({
    width: '100%', minWidth: 0, minHeight: 48, padding: '10px 6px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
    fontWeight: 700, fontSize: 15, lineHeight: 1.2, wordBreak: 'break-word',
    border: `2px solid ${ativo ? '#2b6cb0' : comErro ? '#fc8181' : '#e2e8f0'}`,
    background: ativo ? '#ebf8ff' : '#fff', color: ativo ? '#1e4e8c' : '#2d3748',
});
const opcaoBtn = (ativo) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%', minHeight: 50, textAlign: 'left',
    padding: '12px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
    border: `2px solid ${ativo ? '#2b6cb0' : '#e2e8f0'}`, background: ativo ? '#ebf8ff' : '#fff', color: ativo ? '#1e4e8c' : '#2d3748',
});
const opcaoSub = { fontSize: 13, fontWeight: 400, color: '#4a5568' };
const tabsBox = { display: 'flex', gap: 6, background: '#e2e8f0', padding: 4, borderRadius: 12, marginBottom: 16 };
const tabBtn = (ativo) => ({
    flex: 1, minHeight: 44, border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer',
    background: ativo ? '#fff' : 'transparent', color: ativo ? '#1a202c' : '#4a5568',
    boxShadow: ativo ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
});
const badge = (bg, fg) => ({ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const avisoVermelho = {
    background: '#fff5f5', border: '1px solid #feb2b2', color: '#9b2c2c', borderRadius: 10,
    padding: '12px 14px', fontWeight: 700, fontSize: 14, lineHeight: 1.45, textAlign: 'left', marginBottom: 14,
};
const linhaTestemunha = { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, marginBottom: 8 };
const btnSec = {
    width: '100%', minHeight: 44, border: '1px dashed #a0aec0', background: '#fff', color: '#2d3748',
    borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const btnMini = { border: '1px solid #cbd5e0', background: '#fff', borderRadius: 8, padding: '8px 14px', minHeight: 44, fontSize: 13, cursor: 'pointer' };
const btnIrErro = {
    minHeight: 44, padding: '8px 12px', borderRadius: 8, border: '1px solid #f5c6cb', background: '#fff',
    color: '#721c24', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
};
const btnRemover = {
    flexShrink: 0, width: 44, height: 44, border: '1px solid #feb2b2', background: '#fff5f5', color: '#c53030',
    borderRadius: 8, fontWeight: 700, fontSize: 16, cursor: 'pointer',
};
const btnFoto = (cor) => ({ flex: 1, minHeight: 48, background: cor, color: '#fff', padding: '12px 0' });
// Área de toque de 44px com a bolinha vermelha (30px) ao canto da miniatura
const btnRemoverFoto = {
    position: 'absolute', top: -10, right: -10, width: 44, height: 44, padding: 0, border: 'none', background: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const btnRemoverFotoBola = {
    width: 30, height: 30, borderRadius: '50%', border: '2px solid #fff', background: '#e53e3e', color: '#fff',
    fontWeight: 700, fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};
const vazio = { color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '28px 0' };
